/**
 * data.ts — Helpers de formatação pt-BR (fonte única).
 * Regra do projeto: .claude/docs/projeto/07-formatos-i18n.md
 * Toda a UI formata data/hora/dinheiro/número por aqui — ninguém formata "na mão".
 *
 * Três tipos temporais, três tratamentos distintos:
 *   data pura  "2026-07-14"            -> formatarData      (dayjs: meia-noite LOCAL)
 *   data+hora  "2026-07-14T14:30:00"   -> formatarDataHora  (naive = Brasília)
 *   hora pura  "14:30"                 -> formatarHora      (string, nunca vira Date)
 *
 * O backend serializa datetime naive, sem "Z" e sem offset (db_mysql.py), porque a
 * conexão fixa a sessão em -03:00. Por isso interpretamos tudo como horário local.
 *
 * Por que dayjs e não `new Date`: o JS parseia ISO COM hora como local e SEM hora
 * como UTC. Essa inconsistência da própria linguagem fazia toda data pura aparecer
 * um dia antes em UTC-3. O dayjs trata os dois casos como local.
 */
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isoWeek from 'dayjs/plugin/isoWeek';

dayjs.extend(customParseFormat);
dayjs.extend(isoWeek);

const LOCALE = 'pt-BR';

const fmtDiaSemana = new Intl.DateTimeFormat(LOCALE, { weekday: 'short' });
const fmtDiaMes = new Intl.DateTimeFormat(LOCALE, {
  day: '2-digit', month: '2-digit',
});
const fmtCompleto = new Intl.DateTimeFormat(LOCALE, {
  weekday: 'long', day: '2-digit', month: 'long', year: 'numeric',
});

/** Entrada temporal aceita pelos helpers de data. */
type EntradaData = string | number | Date | null | undefined;

const INVALIDA = '—';

/** "2026-07-14" | "2026-07-14T14:30:00" | Date -> dayjs local (nunca UTC). */
const toDayjs = (v: Exclude<EntradaData, null | undefined>) => dayjs(v);

/** "2026-07-14" -> "14/07/2026" */
export const formatarData = (v: EntradaData): string => {
  if (v == null || v === '') return INVALIDA;
  const d = toDayjs(v);
  return d.isValid() ? d.format('DD/MM/YYYY') : INVALIDA;
};

/** "2026-07-14T14:30:00" -> "14/07/2026 14:30" */
export const formatarDataHora = (v: EntradaData): string => {
  if (v == null || v === '') return INVALIDA;
  const d = toDayjs(v);
  return d.isValid() ? d.format('DD/MM/YYYY HH:mm') : INVALIDA;
};

/**
 * "14:30" | "14:30:00" -> "14:30" (24h).
 * Hora pura não é um instante no tempo — não existe "14:30" sem um dia. Por isso
 * validamos e cortamos a string, em vez de construir um Date.
 * Também aceita data+hora ou Date, extraindo só a hora.
 */
export const formatarHora = (v: EntradaData): string => {
  if (v == null || v === '') return INVALIDA;
  if (typeof v === 'string') {
    const h = dayjs(v, ['HH:mm:ss', 'HH:mm'], true);
    if (h.isValid()) return h.format('HH:mm');
  }
  const d = toDayjs(v);
  return d.isValid() ? d.format('HH:mm') : INVALIDA;
};

/** "2026-07-14" -> "seg" (dia da semana abreviado). */
export const formatarDiaSemana = (v: EntradaData): string => {
  if (v == null || v === '') return INVALIDA;
  const d = toDayjs(v);
  return d.isValid() ? fmtDiaSemana.format(d.toDate()) : INVALIDA;
};

/** "2026-07-14" -> "14/07" (sem o ano — cabeçalho de coluna, card). */
export const formatarDiaMes = (v: EntradaData): string => {
  if (v == null || v === '') return INVALIDA;
  const d = toDayjs(v);
  return d.isValid() ? fmtDiaMes.format(d.toDate()) : INVALIDA;
};

/** "2026-07-14" -> "terça-feira, 14 de julho de 2026". */
export const formatarDataExtenso = (v: EntradaData): string => {
  if (v == null || v === '') return INVALIDA;
  const d = toDayjs(v);
  return d.isValid() ? fmtCompleto.format(d.toDate()) : INVALIDA;
};

/**
 * "há 5 min" / "agora" — tempo desde um datetime da API. Cai para a data cheia
 * depois de uma semana, quando "há 9 d" já não diz nada útil.
 */
export const tempoRelativo = (v: EntradaData): string => {
  if (v == null || v === '') return '';
  const d = toDayjs(v);
  if (!d.isValid()) return '';
  const s = dayjs().diff(d, 'second');
  if (s < 60) return 'agora';
  if (s < 3600) return `há ${Math.floor(s / 60)} min`;
  if (s < 86400) return `há ${Math.floor(s / 3600)} h`;
  if (s < 604800) return `há ${Math.floor(s / 86400)} d`;
  return formatarData(v);
};

/** Date | "2026-07-14" -> "2026-07-14" (ISO local, formato que a API fala). */
export const paraISO = (v: EntradaData): string => {
  if (v == null || v === '') return '';
  const d = toDayjs(v);
  return d.isValid() ? d.format('YYYY-MM-DD') : '';
};

/** Hoje como "2026-07-14" (ISO local, sem surpresa de fuso). */
export const hojeISO = (): string => dayjs().format('YYYY-MM-DD');

/** "2026-07-14" + n dias -> ISO local. n negativo anda pra trás. */
export const addDias = (iso: string, n: number): string =>
  dayjs(iso).add(n, 'day').format('YYYY-MM-DD');

/** Segunda-feira da semana do ISO informado (ISO 8601: semana começa na segunda). */
export const inicioSemana = (iso: string): string =>
  dayjs(iso).startOf('isoWeek').format('YYYY-MM-DD');

/** "2026-07-14" -> Date local (meia-noite local). Use só quando precisar de Date. */
export const paraDate = (v: EntradaData): Date | null => {
  if (v == null || v === '') return null;
  const d = toDayjs(v);
  return d.isValid() ? d.toDate() : null;
};

/** 1500.9 -> "R$ 1.500,90" */
